import { useState } from "react";
import { resizeUploadedFile, dataURLToBlob, urlToFile } from "../CommonUtils";
import { TRYON_SERVER_URL } from "../config";
import { useSelector } from "react-redux";
import { selectAccessToken } from "../selectors";
import { X } from "lucide-react";

export default function UploadImage({ closeModal, imageType, allImages, setAllImages }) {
    const [selectedImage, setSelectedImage] = useState(null);
    const accessToken = useSelector(selectAccessToken) || null;
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(false);
    const [uploadErrorMessage, setUploadErrorMessage] = useState("");

    const handleFileSelected = (event) => {
        const files = event.target.files;
        if (!files || !files[0]) return;
        setSelectedImage(URL.createObjectURL(files[0]));
        setUploadErrorMessage("");
        setUploadError(false);
    };

    const handleImageUpload = async () => {
        setUploadError(false);
        setUploadErrorMessage("");

        if (selectedImage === null) {
            setUploadErrorMessage("Choose a photo first.");
            setUploadError(true);
            return;
        }

        setUploading(true);

        const formData = new FormData();
        formData.append("access", "private");
        if (imageType === "garment" || imageType === "model") {
            formData.append("type", imageType);
        }

        try {
            const imageFile = await urlToFile(selectedImage, "image.png");
            const resizedImageUrl = await resizeUploadedFile(imageFile, 1024);
            const resizedFile = dataURLToBlob(resizedImageUrl);
            formData.append("image", resizedFile, "image.png");

            const headers = accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : {};
            const response = await fetch(`${TRYON_SERVER_URL}/api/v1/experiment_image/`, {
                method: "POST",
                headers,
                body: formData,
            });
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                const message = data?.message || data?.image || data?.error || "Could not upload the photo.";
                setUploadError(true);
                setUploading(false);
                setUploadErrorMessage(Array.isArray(message) ? message[0] : message);
                return;
            }

            setSelectedImage(null);
            setAllImages([data, ...(allImages || [])]);
            closeModal();
        } catch (error) {
            setUploadError(true);
            setUploading(false);
            setUploadErrorMessage("Could not upload the photo.");
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
            onClick={closeModal}
        >
            <div
                className="max-h-[calc(100vh-3rem)] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-[0_0_0_1px_var(--hairline)]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="font-Mulish text-lg font-semibold text-secondary">Upload a photo</h2>
                    <button
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-[0_0_0_1px_var(--hairline)]"
                        aria-label="Close"
                        onClick={closeModal}
                    >
                        <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>

                {selectedImage !== null ? (
                    <div className="relative rounded-2xl bg-primary-50 p-2">
                        <img
                            src={selectedImage}
                            className="mx-auto max-h-72 w-full rounded-lg object-contain"
                            alt="Selected photo"
                        />
                        <button
                            type="button"
                            aria-label="Remove photo"
                            onClick={() => setSelectedImage(null)}
                            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white font-Mulish text-sm text-primary-700 shadow-[0_0_0_1px_var(--hairline)]"
                        >
                            ×
                        </button>
                    </div>
                ) : (
                    <label
                        htmlFor="dropzone-file"
                        className="flex h-64 cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-primary-200 bg-primary-50"
                    >
                        <svg
                            className="mb-3 h-8 w-8 text-primary-700"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 20 16"
                        >
                            <path
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                                d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                            />
                        </svg>
                        <p className="font-Mulish text-sm font-semibold text-secondary">Choose a photo</p>
                        <p className="mt-1 font-Mulish text-xs text-graphite">PNG, JPG, or WEBP</p>
                        <input
                            id="dropzone-file"
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            className="hidden"
                            onChange={handleFileSelected}
                        />
                    </label>
                )}

                <p className="mt-3 font-Mulish text-sm text-graphite">
                    The photo is saved to your gallery.
                </p>

                <button
                    type="button"
                    disabled={uploading || selectedImage === null}
                    className={`mt-4 w-full rounded-full px-4 py-2.5 font-Mulish text-sm font-semibold ${
                        uploading || selectedImage === null
                            ? "bg-primary-100 text-primary-800"
                            : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                    }`}
                    onClick={handleImageUpload}
                >
                    {uploading ? "Uploading..." : "Upload"}
                </button>

                {uploadError && (
                    <p className="mt-3 border-l-2 border-primary-700 pl-3 font-Mulish text-sm text-secondary">
                        {uploadErrorMessage}
                    </p>
                )}
            </div>
        </div>
    );
}
